import React, { useCallback, useEffect, useState } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  IFlowModelDeletedMessage,
  IFlowModelPublishedMessage,
  IFlowModelPublishRequestedMessage,
  IProcedureDeletedMessage,
  IProcedurePublishedMessage,
  IProcedurePublishRequestedMessage
} from '@Shared.Angular/@types/pubSub';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { Category } from '@Client/runner.directives/common/Category/Category';
import ProcessReviewDueList from './Lists/ProcessReviewDueList/ProcessReviewDueList';
import MyApprovalList from './Lists/ApprovalList/MyApprovalList/MyApprovalList';
import SentForApprovalList from './Lists/ApprovalList/SentForApprovalList/SentForApprovalList';
import AllApprovalList from './Lists/ApprovalList/AllApprovalList/AllApprovalList';
import { CustomTabs } from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import PublishRequestFilter from '@Shared.Angular/@types/core/contracts/constants/publishRequestFilter';
import IPublishRequestOverview from '@Shared.Angular/@types/core/contracts/queryModel/governance/publishRequestOverview';
import IReviewDueOverview from '@Shared.Angular/@types/core/contracts/queryModel/governance/reviewDueOverview';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';

type Props = {
  isMobile: boolean;
  backFromState: string;
  onListItemClicked: (id: Guid, loadPublishRequestSchema: boolean) => void;
};

export const Governance = (props: Props) => {
  const { isMobile } = props;
  type IGovernanceItem = IPublishRequestOverview | IReviewDueOverview;
  const governanceLoadProcessReviewDueList =
    'governanceLoadProcessReviewDueList';
  const governanceLoadMyApprovalsList = 'governanceLoadMyApprovalsList';
  const governanceSentForApprovalList = 'governanceSentForApprovalList';
  const allCategoriesId = 'all-categories';
  const appConfig = getService('APP_CONFIG');
  const lodashService = getService('lodashService');
  const pubSubService = getService('pubsubService');
  const appInsightsService = getService('appInsightsService');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const governanceApiService = getService('governanceApiService');

  const [selectedCategoryId, setSelectedCategoryId] = useState(allCategoriesId);
  const [categorisedItems, setCategorisedItems] = useState<
    ITreeCategory<IGovernanceItem>[]
  >([]);
  const [filteredByCategoryItems, setFilteredByCategoryItems] = useState<
    IGovernanceItem[]
  >([]);
  const [filteredItems, setFilteredItems] = useState<IGovernanceItem[]>([]);

  const [selectedTabId, setSelectedTabId] = useState<GovernanceTabId | null>(
    null
  );
  const [searchPlaceholder, setSearchPlaceholder] = useState('');
  const [searchItem, setSearchItem] = useState('');

  useEffect(() => {
    fetchCategorizedItems(selectedTabId);
  }, [selectedTabId]);

  useEffect(() => {
    if (!categorisedItems) {
      return;
    }
    filterItemsByCategory(selectedCategoryId);
  }, [categorisedItems]);

  useEffect(() => {
    filterAction();
  }, [filteredByCategoryItems, selectedCategoryId, searchItem]);

  const fetchAllApprovalsFlowModels = async () => {
    appInsightsService.startEventTimer(governanceLoadMyApprovalsList);
    const categorisedFlowModels =
      await governanceApiService.getCategorizedPublishRequests(
        PublishRequestFilter.All
      );

    setCategorisedItems(
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceLoadMyApprovalsList);
  };

  const fetchMyApprovalsFlowModels = async () => {
    appInsightsService.startEventTimer(governanceLoadMyApprovalsList);
    const categorisedFlowModels =
      await governanceApiService.getCategorizedPublishRequests(
        PublishRequestFilter.WaitingOnMe
      );

    setCategorisedItems(
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceLoadMyApprovalsList);
  };

  const fetchSentForApprovalFlowModels = async () => {
    appInsightsService.startEventTimer(governanceSentForApprovalList);
    const categorisedFlowModels =
      await governanceApiService.getCategorizedPublishRequests(
        PublishRequestFilter.CreatedByMe
      );

    setCategorisedItems(
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceSentForApprovalList);
  };

  const fetchReviewDueItems = async () => {
    appInsightsService.startEventTimer(governanceLoadProcessReviewDueList);
    const categorizedReviewDueItems =
      await governanceApiService.getCategorizedReviewDueItems(false);

    setCategorisedItems(
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        categorizedReviewDueItems
      )
    );
    appInsightsService.trackMetricIfTimerExist(
      governanceLoadProcessReviewDueList
    );
  };

  const filterItemsByCategory = (id: string, categoryIndex?: string) => {
    setSelectedCategoryId(id);

    if (id === allCategoriesId) {
      const filteredItems = extractItemsFromCategories(categorisedItems);
      setFilteredByCategoryItems(filteredItems);
    } else {
      const filteredCategories = runnerCategoryNavigationService.findNodeById(
        categorisedItems,
        id
      );
      setFilteredByCategoryItems(filteredCategories.flowModels);
    }
  };

  const extractItemsFromCategories = (
    categories: ITreeCategory<IGovernanceItem>[]
  ) => {
    let result: IGovernanceItem[] = [];
    categories.forEach((category) => {
      if (category.flowModels?.length > 0) {
        result = result.concat(category.flowModels);
      }
      if (category.items?.length > 0) {
        result = result.concat(extractItemsFromCategories(category.items));
      }
    });

    return result;
  };

  const updateFlowModelName = (jsonParams: string) => {
    if (!Array.isArray(categorisedItems)) {
      return;
    }

    const params = JSON.parse(jsonParams);
    const updatedMap = updateGovernanceItemNameInCategorisedData(
      categorisedItems,
      params.id,
      params.name
    );
    if (updatedMap) {
      setCategorisedItems([...categorisedItems]);
    }
  };

  const refresh = () => {
    fetchCategorizedItems(selectedTabId, selectedCategoryId, searchItem);
  };

  const updateGovernanceItemNameInCategorisedData = (
    categorisedGovernanceItems: ITreeCategory<IGovernanceItem>[],
    entityId: Guid,
    nameToBeUpdated: string
  ) => {
    for (const category of categorisedGovernanceItems) {
      const entity = category.flowModels.find(
        (x) =>
          ('id' in x && x.id === entityId) ||
          (x as IPublishRequestOverview).entityId === entityId
      );
      if (entity && 'name' in entity) {
        entity.name = nameToBeUpdated;
        return entity;
      } else if (entity && 'entityName' in entity) {
        entity.entityName = nameToBeUpdated;
        return entity;
      }

      if (category.items) {
        const foundItem = updateGovernanceItemNameInCategorisedData(
          category.items,
          entityId,
          nameToBeUpdated
        );
        if (foundItem) {
          return foundItem;
        }
      }
    }
    return undefined;
  };

  const handleSearchChange = (value: string) => setSearchItem(value);

  const executeAllApprovalsFilter = () =>
    executeFilter(
      ['entityDescription', 'entityName', 'creatorName'],
      'ApprovalDueUtc'
    );
  const executeMyApprovalsFilter = () =>
    executeFilter(
      ['entityDescription', 'entityName', 'creatorName'],
      'ApprovalDueUtc'
    );
  const executeSentForApprovalFilter = () =>
    executeFilter(
      ['entityDescription', 'entityName', 'approverName'],
      'ApprovalDueUtc'
    );
  const executeProcessReviewDueFilter = () =>
    executeFilter(['description', 'name'], 'processOverdueOnUtc');

  const executeFilter = (searchFields: string[], sortBy: string) => {
    const filteredResults = !searchItem
      ? filteredByCategoryItems
      : filteredByCategoryItems.filter((x) =>
          searchFields.some((field) =>
            x[field]?.toLowerCase().includes(searchItem.toLowerCase())
          )
        );
    const ordered = lodashService.orderBy(filteredResults, [sortBy], ['asc']);
    setFilteredItems(ordered);
  };

  const permissionsService = getService('permissionsService');
  const isApprover = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISHER_CANDIDATE
  );
  const hasAllApprovalAccess = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISH_ALL_ACCESS
  );
  const hasPublishPermission =
    permissionsService.currentUserHasAnyOfPermissions([
      FlowinglyPermissions.FLOWMODEL_PUBLISH,
      FlowinglyPermissions.FLOWMODEL_PUBLISH_OWN
    ]);

  const isEditor =
    permissionsService.currentUserHasPermission(
      FlowinglyPermissions.GOVERNANCE_ACCESS
    ) &&
    !isApprover &&
    !hasAllApprovalAccess &&
    !hasPublishPermission;

  const [hasSentForApprovalData, setHasSentForApprovalData] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const data = await governanceApiService.getPublishRequests(
          PublishRequestFilter.CreatedByMe
        );
        setHasSentForApprovalData(!!data?.length);
      } catch (error) {
        setHasSentForApprovalData(false);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  const hideSentForApprovalTab = !isEditor && !hasSentForApprovalData;

  const tabs = [
    {
      id: GovernanceTabId.ALL_APPROVALS,
      label: 'All publish approvals',
      isHidden: !hasAllApprovalAccess,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or author'
        );
        await fetchAllApprovalsFlowModels();
      },
      filter: () => executeAllApprovalsFilter(),
      render: (listProps) => <AllApprovalList {...listProps} />
    },
    {
      id: GovernanceTabId.MY_APPROVALS,
      label: 'My publish approvals',
      isHidden: !isApprover,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or author'
        );
        await fetchMyApprovalsFlowModels();
      },
      filter: () => executeMyApprovalsFilter(),
      render: (listProps) => <MyApprovalList {...listProps} />
    },
    {
      id: GovernanceTabId.SENT_FOR_APPROVAL,
      label: 'Sent for publish approval',
      isHidden: hideSentForApprovalTab,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or category publisher'
        );
        await fetchSentForApprovalFlowModels();
      },
      filter: () => executeSentForApprovalFilter(),
      render: (listProps) => <SentForApprovalList {...listProps} />
    },
    {
      id: GovernanceTabId.PROCESS_REVIEW_DUE,
      label: 'Process review due',
      fetch: async () => {
        setSearchPlaceholder('Search by name or description');
        await fetchReviewDueItems();
      },
      filter: () => executeProcessReviewDueFilter(),
      render: (listProps) => <ProcessReviewDueList {...listProps} />
    }
  ];

  const fetchCategorizedItems = async (
    tabId: GovernanceTabId,
    category = null,
    searchText = null
  ) => {
    if (category == null) {
      setSelectedCategoryId(allCategoriesId);
    }
    if (searchText == null) {
      setSearchItem('');
    }

    await tabs.find((tab) => tab.id === tabId)?.fetch();
  };

  const filterAction = useCallback(() => {
    tabs.find((tab) => tab.id === selectedTabId)?.filter();
  }, [selectedTabId, tabs]);

  const removeItem = useCallback(
    (entityId: Guid) => {
      const removed = runnerCategoryNavigationService.removeItem(
        categorisedItems,
        entityId
      );
      if (removed) {
        setCategorisedItems([...categorisedItems]);
        filterAction();
      }
    },
    [categorisedItems, filterAction]
  );

  const addPublishRequest = useCallback(
    (publishRequest: IPublishRequestOverview) => {
      setCategorisedItems((prevCategories) => {
        runnerCategoryNavigationService.addItem(
          prevCategories,
          allCategoriesId,
          publishRequest
        );
        return [...prevCategories];
      });
    },
    [allCategoriesId]
  );

  const addOrReplacePublishRequest = useCallback(
    (publishRequest: IPublishRequestOverview) => {
      const userId = getService('sessionService').getUser().id;
      const shouldBeShownInCurrentTab =
        selectedTabId === GovernanceTabId.ALL_APPROVALS ||
        (selectedTabId === GovernanceTabId.MY_APPROVALS &&
          publishRequest.approverId === userId) ||
        (selectedTabId === GovernanceTabId.SENT_FOR_APPROVAL &&
          publishRequest.creatorId === userId);
      if (shouldBeShownInCurrentTab) {
        removeItem(publishRequest.entityId);
        addPublishRequest(publishRequest);
      }
    },
    [categorisedItems, addPublishRequest, removeItem, selectedTabId]
  );

  const governanceSubscriberId = 'flowingly.runner.governance';
  useEffect(() => {
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event, params: string) => updateFlowModelName(params),
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      (event, message: IFlowModelPublishedMessage) =>
        removeItem(message.flowModelId),
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'PROCEDURE_DELETED',
      (event, message: IProcedureDeletedMessage) =>
        removeItem(message.procedureId),
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      (event, message: IFlowModelDeletedMessage) =>
        removeItem(message.flowModelId),
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'PROCEDURE_PUBLISHED',
      (event, message: IProcedurePublishedMessage) =>
        removeItem(message.summary.id),
      governanceSubscriberId
    );
    pubSubService.subscribeMany(
      [
        'SIGNALR_FLOW_MODEL_PUBLISH_REQUESTED',
        'SIGNALR_PROCEDURE_PUBLISH_REQUESTED'
      ],
      (
        event,
        message:
          IProcedurePublishRequestedMessage | IFlowModelPublishRequestedMessage
      ) => addOrReplacePublishRequest(message.overview),
      governanceSubscriberId
    );
    pubSubService.subscribeMany(
      ['SIGNALR_SETUP_CATEGORY_DELETED', 'SIGNALR_WORKFLOW_UNPUBLISHED'],
      (event, message) => refresh(),
      governanceSubscriberId
    );
    return () => {
      pubSubService.unsubscribeAll(governanceSubscriberId);
    };
  }, [updateFlowModelName, removeItem, refresh, addOrReplacePublishRequest]);

  const renderSelectedList = () => {
    const listProps = {
      isMobile,
      filteredItems: filteredItems,
      selectedTab: selectedTabId,
      appConfig
    };

    const selectedTab = tabs.find((tab) => tab.id === selectedTabId);
    return selectedTab?.render ? selectedTab.render(listProps) : null;
  };

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (initialized && selectedTabId === null) {
      const firstVisibleTab = tabs.find((tab) => !tab.isHidden);
      if (firstVisibleTab) {
        setSelectedTabId(firstVisibleTab.id);
      }
    }
  }, [initialized, selectedTabId]);

  const handleTabChange = (newTabId: string) => {
    if (newTabId === selectedTabId) {
      return;
    }
    setSelectedTabId(newTabId as GovernanceTabId);
    setSearchItem('');
    setFilteredItems([]);
    setFilteredByCategoryItems([]);
    setSelectedCategoryId(allCategoriesId);
  };

  return (
    <div className="runner-page" id="governance">
      <div className="runner-page__content">
        <Category
          categorisedItems={categorisedItems}
          selectedCategoryId={selectedCategoryId}
          defaultItemId={allCategoriesId}
          addAllCategory={true}
          onCategoryClick={filterItemsByCategory}
        />
        <div className="runner-page__content__main-wrapper">
          <div
            className={`start-category-list runner-page__content__main-content ${
              isMobile ? 'isMobile' : ''
            }`}
          >
            <div className="governance-search-tabs-container">
              <Search
                placeholder={searchPlaceholder}
                value={searchItem}
                onValueChange={handleSearchChange}
              />
              <CustomTabs
                selectedTabId={selectedTabId}
                onChange={handleTabChange}
                tabItems={tabs}
              ></CustomTabs>
            </div>
            {renderSelectedList()}
          </div>
        </div>
      </div>
    </div>
  );
};

enum GovernanceTabId {
  MY_APPROVALS = 'my-approvals',
  ALL_APPROVALS = 'all-approvals',
  SENT_FOR_APPROVAL = 'sent-for-approvals',
  PROCESS_REVIEW_DUE = 'process-review-due'
}
